<template>
  <div class="flex flex-column gap-3">
    <h3 class="m-0">Actions</h3>
    <a-alert
      v-if="loadError"
      type="error"
      show-icon
      message="Actions couldn't be loaded. Close and reopen this shift to try again."
      data-testid="shift-view-actions-error"
    />
    <template v-else>
      <p class="m-0">
        Every action {{ associateName }} takes during a shift is captured automatically — when it happened, what device
        it came from, and where they were at the time.
      </p>
      <div v-if="problems.length" class="flex flex-column gap-2" data-testid="shift-view-actions-problems">
        <button
          v-for="problem in problems"
          :key="problem.id"
          type="button"
          class="problem-button"
          @click="$emit('open-authorization', problem.id)"
        >
          We flagged an issue with this shift: {{ problem.message }}
        </button>
        <a-button
          type="link"
          class="w-fit p-0"
          data-testid="shift-view-actions-authorization-link"
          @click="$emit('open-authorization', problems[0].id)"
        >
          See Violations for details.
        </a-button>
      </div>
      <actions-info :list="actions" />
    </template>
  </div>
</template>

<script>
import Locations from '@/components/locations.vue'
import { Alert, Button } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import { AUTH_STATUSES } from '@/store/modules/shifts/constants'

const { mapState: shiftState } = createNamespacedHelpers('shifts')

export default {
  components: {
    'a-alert': Alert,
    'a-button': Button,
    'actions-info': Locations
  },
  props: {
    loadError: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...shiftState({
      actions: state => state.shiftActions,
      authorizations: state => state.shiftAuthorizations,
      associate: state => state.shift.associate
    }),
    associateName() {
      return `${this.associate.first_name} ${this.associate.last_name}`.trim()
    },
    problems() {
      const relevantKeys = new Set(['outside_work_area_authorization', 'associate_not_recognized_authorization'])

      return this.authorizations
        .filter(item => item.status === 'initial' && !item.deleted_at && relevantKeys.has(item.type))
        .map(item => ({ id: item.id, message: this.getProblemMessage(item.type) }))
    }
  },
  methods: {
    getProblemMessage(type) {
      if (type === 'outside_work_area_authorization') {
        const action = this.actions.find(item => item.in_wa === false)
        if (action) {
          const distance = ((action.distance ?? 0) / 0.9144).toFixed(0)
          return `${this.associateName} ${this.getActionPhrase(action.action)} ${distance} yd outside the work area.`
        }
      }

      if (type === 'associate_not_recognized_authorization') {
        const action = this.actions.find(item => item.recognized === false)
        if (action) {
          return `We couldn't recognize ${this.associateName} in the photo taken at ${this.getActionPhrase(action.action, true)}.`
        }
      }

      return `${AUTH_STATUSES[type]?.title ?? 'A violation'} needs review.`
    },
    getActionPhrase(action, noun = false) {
      const labels = {
        work_start: noun ? 'work start' : 'started work',
        work_end: noun ? 'work end' : 'ended work',
        lunch_start: noun ? 'meal break start' : 'started a meal break',
        lunch_end: noun ? 'meal break end' : 'ended a meal break',
        second_lunch_start: noun ? 'second meal break start' : 'started a second meal break',
        second_lunch_end: noun ? 'second meal break end' : 'ended a second meal break'
      }

      return labels[action] ?? (noun ? 'a shift action' : 'recorded an action')
    }
  }
}
</script>

<style scoped>
::v-deep .ant-table-footer {
  padding: 8px;
}

::v-deep .ant-table-header {
  scrollbar-width: none;
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
  padding-right: 8px;
}

.problem-button {
  width: fit-content;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--error-color);
  text-align: left;
  cursor: pointer;
}

.problem-button:focus-visible {
  outline: 2px solid #1890ff;
  outline-offset: 2px;
}
</style>
